import { SelectChangeEvent } from "@mui/material/Select";
import { useRef, useState, useMemo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import fieldsIcon from "../../assets/icons/fields-icons.svg";
import recordIcon from "../../assets/icons/records-icon.svg";
import {
  usePreviewCount,
  useRowCount,
  useFieldCount,
  useSegmentCount,
} from "../../services/home/index";
import { useCountryList, useAppMe } from "../../services/okta";
import { useStickyHeader } from "../../shared/hooks/StickyHeader";
import { CircularProgressCustom } from "../../utils/muiCustomTheme";
import ConsumerProfile, { ConsumerDetails } from "./ConsumerProfile";
import DatasetTables from "./DatasetTable";
import GridContainer from "./GridContainer";
import DatasetDropdownHome from "../../shared/components/dataset-dropdown/DatasetDropdownHome";
import { formatNumbers } from "../../utils/numberFormatters";
import "./Home.scss";

// Helper to parse JSON safely
const parseData = (data: any) => {
  try {
    return JSON.parse(data || "[]");
  } catch {
    return [];
  }
};

// Format username for display
const formatUsername = (username: string) => username.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

function Home() {
  const tablesSectionRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { headerRef, sentinelRef, isSticky } = useStickyHeader();

  const [selectedDataset, setSelectedDataset] = useState<string>(
    location.state?.selectedDataset || ""
  );

  // Fetch user info
  const { data: dataAppMe, isPending: isPendingAppMe } = useAppMe({});
  const { data: countries = [], isLoading: isCountriesLoading } = useCountryList();

  useEffect(() => {
    // Set default dataset only if one is not already selected and countries have loaded
    if (countries.length > 0 && !selectedDataset) {
      setSelectedDataset(countries[0].code);
    }
  }, [countries]);

  const selectedCountryName = useMemo(() => {
    return countries?.find((c: { code: string; }) => c.code === selectedDataset)?.name || "";
  }, [countries, selectedDataset]);

  const countryTags = Array.from(new Set(countries?.flatMap((c) => c.tags) || []));


  // Memoize query arguments to prevent re-fetching on every render
  const queryArgsCountryCode = useMemo(() => ({ countryCode: selectedDataset }), [selectedDataset]);
  const queryArgsCountryName = useMemo(() => ({
    country: selectedCountryName,
    tags: countryTags }),
  [selectedCountryName, countryTags]);

  // Enable base queries only when a dataset is selected and countries have loaded
  const baseQueriesEnabled = !isCountriesLoading && !!selectedDataset;
  // Enable segment query only when the country name is also available
  const segmentQueryEnabled = baseQueriesEnabled && !!selectedCountryName && countryTags.length > 0;

  const { data: dataRowCount, isPending: isPendingRows } = useRowCount({
    query: queryArgsCountryCode, enabled: baseQueriesEnabled,
  });

  const { data: dataFieldCount, isPending: isPendingFields } = useFieldCount({
    query: queryArgsCountryCode, enabled: baseQueriesEnabled,
  });

  const { data: dataSegmentCount, isPending: isPendingSegments } = useSegmentCount({
      query: queryArgsCountryName, enabled: segmentQueryEnabled,
  });

  const { data: dataPreviewCount, isPending: isPendingPreview } = usePreviewCount({
      query: queryArgsCountryCode, enabled: baseQueriesEnabled,
  });

  const sub = dataAppMe?.data?.user_name || "";
  const ccdp_groups = dataAppMe?.data.ccdp_groups ?? [];
  const isDeveloper = ccdp_groups.some((g: string)=> g.toLowerCase().includes("developer"));
  const isGlobalAdmin = ccdp_groups.some((g: string)=> g.toLowerCase().includes("global_admin"));
  const isMarketerAdmin = ccdp_groups.some((g: string)=> g.toLowerCase().includes("marketer_admin"));

  type UserRole = "Marketer" | "Marketer admin" | "Global Admin";
  let userRole: UserRole = "Marketer";
  if (isMarketerAdmin) {
      userRole = "Marketer admin";  }
  if (isDeveloper || isGlobalAdmin) {
    userRole = "Global Admin";
  }

  // Parse row, field, and segment data
  const parsedRowData = parseData(dataRowCount?.data).map((item: any) => {
    const table_name = Object.keys(item)[0];
    const row_count = item[table_name];
    return { table_name, row_count };
  });

  const fieldData = parseData(dataFieldCount?.data);
  const segmentData = parseData(dataSegmentCount?.data);
  const segmentCount = segmentData[0]?.segment_count ?? 0;

  // Consumer 360 data
  const consumerProfilesRaw = Array.isArray(dataPreviewCount?.data)
    ? dataPreviewCount.data
    : [];
  const consumerProfiles = consumerProfilesRaw.find(
    (item: any) => item.username === "num_profiles"
  );
  const transformedConsumerData: ConsumerDetails[] = consumerProfilesRaw
    .filter((item: any) => item.username !== "num_profiles")
    .map((item: any) => ({
      username: formatUsername(item.username),
      percent: item.percent,
      status: item.status,
      progress: item.progress,
    }));

  // Transform tables for Grid
  const transformedTablesData = parsedRowData // Input data from the API (rows + table metadata).
    .filter((rowItem: any) => {
      const rawName = typeof rowItem?.table_name === "string" ? rowItem.table_name.trim() : "";
      if (!rawName) return false;
      const normalizedName = rawName.replace(/entity_|_table/gi, "").trim();
      if (!normalizedName) return false;
      const datasetCodeUs = selectedDataset.trim().toLowerCase() === "us";
      const isStirista = normalizedName.toLowerCase() === "stirista";
      return !isStirista || datasetCodeUs;
    }) // Keep only items with a non-empty table name; hide "Address" for non-USA datasets.
    .map((rowItem: any) => { // Convert each API row into the UI model used by the grid/cards.
      const fieldItem = fieldData.find( // Find the matching "field count" entry for this table.
        (f: any) => // Match predicate (case-insensitive by table_name).
          f.table_name.toLowerCase() === rowItem.table_name.toLowerCase() // Compare normalized table names (lowercase).
      ); // Returns the first match or undefined.
      const tableTitle = rowItem.table_name // Take the raw table name and format it for display.
        .replace(/entity_|_table/gi, "") // Remove technical prefixes/suffixes (e.g., entity_, _table).
        .replace(/\b\w/g, (c: string) => c.toUpperCase()); // Convert to Title Case (capitalize each word boundary).
      // Blank line intentionally replaced with this comment so every line has an explanation.
      return { // Build the object used to render the table card/statistics.
        title: tableTitle, // Title displayed in the UI.
        stats: [ // List of stats shown on the card.
          { // Stat: number of records in the table.
            icon: recordIcon, // Icon for "Number of records".
            label: "Number of records", // Stat label.
            value: formatNumbers(rowItem.row_count ?? 0), // Stat value: formatted record count (fallback to 0).
          }, // End of "Number of records" stat.
          { // Stat: number of fields/columns in the table.
            icon: fieldsIcon, // Icon for "Number of fields".
            label: "Number of fields", // Stat label.
            value: fieldItem ? formatNumbers(fieldItem.field_count) : "-", // Stat value: formatted field count or "-" if missing.
          }, // End of "Number of fields" stat.
        ], // End of stats list.
        tableName: tableTitle, // Table identifier used later (currently the same as title).
      }; // End of table object.
    }); // End of map() transformation and the whole chain.

  const cardData = [
    {
      label: "Attributes Tables", value: formatNumbers((selectedDataset.trim().toLowerCase() !== "us" ? (parsedRowData.length - 1) : parsedRowData.length)) || 0
    },
    {
      label: "Consumer 360 Profiles",
      value: consumerProfiles ? formatNumbers(consumerProfiles.percent) : 0,
      route: "/view-all-attributes",
    },
    {
      label: "Segments", value: formatNumbers(segmentCount), route: "/segments"
    },
  ];

  return (
    <div className="home">
      <div ref={sentinelRef}></div>

      <div ref={headerRef} className={`home__header ${isSticky ? "is-sticky" : ""}`}>
        <div className="home__heading-wrap">
          <div className="home__heading-wrap__username">
            {isPendingAppMe ? "Loading..." : `Hi, ${sub}`}
          </div>
          <div className="home__heading-wrap__role">{isPendingAppMe ? "Loading..." : `${userRole}`}</div>
        </div>

        <div className="home__dataset-sec">
          <div className="view-all-attributes__dataset-wrapper__label">Market:</div>
          {isCountriesLoading ? (
            <CircularProgressCustom size={20} />
          ) : (
            <DatasetDropdownHome
              value={selectedDataset}
              onChange={(e: SelectChangeEvent) => setSelectedDataset(e.target.value)}
              countries={countries}
            />
          )}
        </div>

        <div className="create-segment__header-divider"></div>
      </div>
      <div className="home__dataset-wrap">
        {isPendingRows || isPendingFields || isPendingSegments ? (
          <CircularProgressCustom size={20} />
        ) : (
            <DatasetTables
              onScrollToTables={() => {
                if (tablesSectionRef.current) {
                  const top = tablesSectionRef.current.getBoundingClientRect().top + window.scrollY;
                  window.scrollTo({
                    top: top - 80,
                    behavior: "smooth",
                  });
                }
              }}
              cardData={cardData}
              onCardClick={(cardLabel: string, route?: string) => {
                if (!route) return;

                if (cardLabel === "Consumer 360 Profiles") {
                  navigate(route, { state: { tableName: "Consumer", selectedDataset } });
                } else if (cardLabel === "Segments") {
                  navigate(route, { state: { selectedDataset } });
                } else {
                  navigate(route, { state: { tableName: cardLabel, selectedDataset } });
                }
              }}
            />
        )}
      </div>

      <div className="home__table-wrap">
        <div className="home__table-wrap__head">
          <div>
            <div className="home__table-wrap__label">Consumer 360 Profile</div>
            <div className="home__table-wrap__desc">
              Explore the attributes of your Consumer 360
            </div>
          </div>
        </div>

        {isPendingPreview ? (
          <CircularProgressCustom size={20} />
        ) : (
          <ConsumerProfile consumerProfileDetails={transformedConsumerData} />
        )}
      </div>

      <div className="home__table-wrap" ref={tablesSectionRef}>
        <div className="home__table-wrap__label">Attributes Tables</div>
        {isPendingRows || isPendingFields ? (
          <CircularProgressCustom size={20} />
        ) : (
          <GridContainer data={transformedTablesData} selectedDataset={selectedDataset} />
        )}
      </div>
    </div>
  );
}

export default Home;
